import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Truck,
  CheckCircle2,
  Clock,
  MapPin,
  Copy,
  Package,
  RefreshCw,
  Loader2,
  Box,
} from 'lucide-react'
import { toast } from 'sonner'
import { useShipmentTracking } from '../hooks/use-shipping'


interface TrackingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  awbNumber?: string | null
  orderNumber?: string
  orderId?: number
  isAdmin?: boolean
}


const MILESTONES = [
  { key: 'MANIFESTED', label: 'Manifested / Ready', icon: Box },
  { key: 'PICKED_UP', label: 'Picked Up', icon: Package },
  { key: 'IN_TRANSIT', label: 'In Transit', icon: Truck },
  { key: 'OUT_FOR_DELIVERY', label: 'Out for Delivery', icon: Truck },
  { key: 'DELIVERED', label: 'Delivered', icon: CheckCircle2 },
]

export function TrackingDialog({
  open,
  onOpenChange,
  awbNumber,
  orderNumber,
  orderId,
}: TrackingDialogProps) {
  const { data: tracking, isLoading, refetch, isFetching } = useShipmentTracking(
    open ? awbNumber : null
  )

  const handleCopyAwb = () => {
    if (awbNumber) {
      navigator.clipboard.writeText(awbNumber)
      toast.success('AWB Number copied!')
    }
  }

  const currentStatus = (tracking?.currentStatus || 'IN_TRANSIT').toUpperCase()

  const getMilestoneIndex = (status: string) => {
    const s = status.toUpperCase()
    if (s.includes('DELIVERED')) return 4
    if (s.includes('OUT') || s.includes('DELIVERY')) return 3
    if (s.includes('TRANSIT')) return 2
    if (s.includes('PICK')) return 1
    return 0
  }

  const activeIndex = getMilestoneIndex(currentStatus)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto">
        <DialogHeader className="border-b border-border pb-3">
          <div className="flex items-center justify-between gap-3 pr-6">
            <div className="flex items-center gap-2">
              <div className="size-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
                <Truck className="size-5" />
              </div>
              <div>
                <DialogTitle className="font-display text-lg font-bold">
                  Bigship Live Tracking
                </DialogTitle>
                <p className="text-xs text-muted-foreground">
                  Order #{orderNumber || 'Spare Part'}
                </p>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              className="h-8 gap-1.5 text-xs font-semibold"
            >
              <RefreshCw className={`size-3.5 ${isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          {/* AWB & Courier Card */}
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="border-primary/30 text-primary font-mono text-[11px]">
                  Bigship Logistics
                </Badge>
                <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 font-bold text-[11px]">
                  {tracking?.courierPartner || 'Delhivery Express Surface'}
                </Badge>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <span className="text-xs text-muted-foreground font-semibold">AWB:</span>
                <span className="font-mono text-base font-extrabold text-foreground tracking-wide">
                  {awbNumber || tracking?.awbNumber || 'PENDING'}
                </span>
                {awbNumber && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 text-muted-foreground hover:text-foreground"
                    onClick={handleCopyAwb}
                    title="Copy AWB"
                  >
                    <Copy className="size-3.5" />
                  </Button>
                )}
              </div>
            </div>

            <div className="text-left sm:text-right">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold block">
                Current Status
              </span>
              <Badge className="mt-1 bg-primary text-white font-bold text-xs py-1 px-3">
                {currentStatus.replace(/_/g, ' ')}
              </Badge>
            </div>
          </div>

          {/* Stepper Progress */}
          <div className="py-2">
            <div className="relative flex items-center justify-between">
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-muted -z-0" />
              <div
                className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-primary transition-all duration-500 -z-0"
                style={{
                  width: `${(activeIndex / (MILESTONES.length - 1)) * 100}%`,
                }}
              />

              {MILESTONES.map((m, idx) => {
                const Icon = m.icon
                const isPassed = idx <= activeIndex
                const isCurrent = idx === activeIndex

                return (
                  <div key={m.key} className="relative z-10 flex flex-col items-center">
                    <div
                      className={`size-8 rounded-full flex items-center justify-center transition-all ${
                        isPassed
                          ? 'bg-primary text-white ring-4 ring-primary/20 font-bold shadow-xs'
                          : 'bg-muted text-muted-foreground border border-border'
                      }`}
                    >
                      <Icon className="size-4" />
                    </div>
                    <span
                      className={`text-[11px] font-semibold mt-2 text-center max-w-[70px] leading-tight ${
                        isCurrent
                          ? 'text-primary font-bold'
                          : isPassed
                            ? 'text-foreground'
                            : 'text-muted-foreground'
                      }`}
                    >
                      {m.label}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>


          {/* Scans Timeline */}
          <div className="space-y-3">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Clock className="size-3.5 text-primary" /> Tracking History & Scans
            </span>

            {isLoading ? (
              <div className="py-8 text-center flex flex-col items-center justify-center space-y-2">
                <Loader2 className="size-6 animate-spin text-primary" />
                <span className="text-xs text-muted-foreground">Fetching live scans from Bigship...</span>
              </div>
            ) : tracking?.scans && tracking.scans.length > 0 ? (
              <div className="divide-y divide-border rounded-xl border border-border bg-card p-4 space-y-3 max-h-60 overflow-y-auto">
                {tracking.scans.map((scan, idx) => {
                  const dateStr = scan.date
                    ? new Date(scan.date).toLocaleString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : 'Recent'

                  return (
                    <div key={idx} className="pt-3 first:pt-0 flex items-start gap-3 text-xs">
                      <div className="size-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                        <CheckCircle2 className="size-3.5" />
                      </div>
                      <div className="flex-1 space-y-0.5">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-foreground">
                            {scan.status?.replace(/_/g, ' ')}
                          </span>
                          <span className="text-[11px] text-muted-foreground font-mono">
                            {dateStr}
                          </span>
                        </div>
                        <p className="text-foreground/80 leading-relaxed">{scan.activity}</p>
                        {scan.location && (
                          <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                            <MapPin className="size-3" /> {scan.location}
                          </span>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
                No tracking scans recorded yet. Package is awaiting courier pickup.
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
